import { validate, matchers, logReport } from "../validate-props";
/**
 * This script is part of the component initialization workflow.
 *
 * This helpers is designed:
 *  - check all **requiredElements** object contains objects like {query:String,selector:Function}
 *  - warn all properties with falsy values
 *  - returns an array where first element is a boolean indicating success and 2nd an object holding the result of selector
 * The default validation uses **matchers.truthy**
 *
 * The default warn message is: `${componentName}: missing required element ${propName}`.
 *
 * To customize tests or messages it's possible to provide a config object like:
 *  - **componentName**: for better logging
 *  - **customSchema**: object mapping requiredElements that needs different validation
 *  - **customReport**: object mapping requiredElements that needs different warn messages
 *
 * @param {{
 * componentName:string
 * requiredElements:{query:string,selector:any},
 * customSchema?:{object},
 * customReport?:{object}
 * }} config
 *
 * @returns {[boolean,object]}
 */
const validateRequiredElements = ({
    requiredElements,
    customReport = {},
    customSchema = {},
    componentName = "",
}) => {
    // Creates a defaultConfig with default schemas and warn messages for all props in requireElement
    const props = Object.keys(requiredElements);
    if (!props.length) {
        return [false];
    }
    const wellFormedData = props.every((propName) => {
        return (
            matchers.object(requiredElements[propName]) &&
            matchers.string(requiredElements[propName].query)
        );
    });
    if (!wellFormedData) {
        return [false];
    }
    // Create a default matcher to test all props and log it's message using the query value o requiredElements
    const defaultConfig = props.reduce(
        (newObject, propName) => {
            newObject.schema[propName] = matchers.truthy;
            newObject.messages[
                propName
            ] = `${componentName}: error validating required elements. Missing element with selector ${requiredElements[propName].query}`;
            return newObject;
        },
        { messages: {}, schema: {} }
    );

    // Overriding default values with custom in the case they exist
    customReport = Object.assign({}, defaultConfig.messages, customReport);
    customSchema = Object.assign({}, defaultConfig.schema, customSchema);

    const elements = Object.keys(requiredElements).reduce((resultObject, propName) => {
        resultObject[propName] = requiredElements[propName].selector;
        return resultObject;
    }, {});

    const [success, report] = validate(elements, customSchema);

    if (!success) {
        logReport(report, customReport);
        return [false];
    }

    return [true, elements];
};

export default validateRequiredElements;
